import React, { useState, useEffect, memo, useRef } from 'react';
import { ethers } from 'ethers';
import { getFromStorage, saveToStorage, clearSecureStorage } from '../utils/storage';
import { decryptData } from '../utils/crypto';
import { useSnackbar, SnackbarProvider } from 'notistack'; // Import notistack
import { authenticator } from 'otplib';
import {
  Box, Typography, Card, CardContent, Button, TextField, CircularProgress,
  AppBar, Toolbar, IconButton, styled,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Slide, Alert
} from '@mui/material';
import { Key, Wallet, ExitToApp, Lock, Warning, Close } from '@mui/icons-material'; // Removed VolumeUp, VolumeOff
import { Link, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';

// Minimalist theme with elegant subtle effects
const theme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#000000', paper: '#1A1A1A' }, // Darker paper for contrast
    primary: { main: '#FFFFFF', contrastText: '#000000' },
    secondary: { main: '#B0B0B0', contrastText: '#000000' }, // Softer white/grey for secondary
    text: { primary: '#FFFFFF', secondary: '#B0B0B0' },
    divider: '#333333',
    action: {
      active: '#FFFFFF',
      hover: '#2A2A2A',
      selected: '#3A3A3A',
      disabled: '#666666',
      disabledBackground: '#222222'
    },
    error: { main: '#FF6B6B' }, // Slightly softer red for errors
    warning: { main: '#FFD700' }, // Gold for warnings
    info: { main: '#87CEEB' }, // Sky blue for info
    success: { main: '#8BC34A' } // Light green for success
  },
  typography: {
    fontFamily: '"Roboto Mono", monospace', // Changed font for a cleaner look
    h4: { fontSize: '2rem', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase' },
    h5: { fontSize: '1.6rem', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase' },
    h6: { fontSize: '1.2rem', fontWeight: 500, letterSpacing: '1px', textTransform: 'uppercase' },
    body1: { fontSize: '0.95rem', lineHeight: 1.7, letterSpacing: '0.5px' },
    body2: { fontSize: '0.8rem', lineHeight: 1.5, letterSpacing: '0.4px' },
    caption: { fontSize: '0.7rem', letterSpacing: '0.4px' }
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '4px', // Slightly rounded corners for a modern feel
          background: 'rgba(26, 26, 26, 0.95)',
          border: '1px solid #333333',
          boxShadow: '0 4px 15px rgba(0,0,0,0.4)', // More subtle shadow
          transition: 'all 0.3s ease-in-out',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            borderColor: '#FFFFFF',
            boxShadow: '0 6px 20px rgba(0,0,0,0.6)',
            transform: 'translateY(-2px)'
          },
          // Subtle texture/grain effect
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAAFklEQVQIHWNkYGD4zwAFWAEIMmAEWAAAAAxjAYkAAAAASUVORK5CYII=")', // A very subtle noise pattern
            opacity: 0.05,
            pointerEvents: 'none'
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          textTransform: 'uppercase',
          padding: '10px 20px',
          fontWeight: 600,
          letterSpacing: '1.5px',
          background: 'transparent',
          color: '#FFFFFF',
          border: '1px solid #B0B0B0', // Subtler border for minimalist look
          transition: 'all 0.3s ease-in-out',
          willChange: 'transform, background, box-shadow',
          '&:hover': {
            background: '#FFFFFF',
            color: '#000000',
            border: '1px solid #FFFFFF',
            boxShadow: '0 0 15px rgba(255,255,255,0.3)',
            transform: 'translateY(-1px)'
          },
          '&.Mui-disabled': {
            borderColor: '#666666',
            color: '#666666',
            opacity: 0.6
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
            backgroundColor: 'rgba(26, 26, 26, 0.95)',
            '& fieldset': { border: '1px solid #333333' },
            '&:hover fieldset': { borderColor: '#B0B0B0' },
            '&.Mui-focused fieldset': { borderColor: '#FFFFFF', borderWidth: '1px' },
            '& .MuiInputBase-input': {
              fontFamily: '"Roboto Mono", monospace',
              color: '#FFFFFF',
              fontSize: '0.9rem',
              paddingLeft: '12px'
            }
          },
          '& .MuiInputLabel-root': {
            color: '#B0B0B0',
            fontWeight: 500,
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            fontSize: '0.85rem'
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: '#000000',
          borderBottom: '1px solid #FFFFFF',
          boxShadow: 'none'
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'rgba(10, 10, 10, 0.98)',
          border: '1px solid #333333',
          borderRadius: '4px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: 'rgba(10, 10, 10, 0.98)',
          border: '1px solid #333333',
          borderRadius: '4px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
        }
      }
    }
  }
});

// Styled Card for consistent appearance
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '4px',
  background: 'rgba(26, 26, 26, 0.95)',
  border: '1px solid #333333',
  boxShadow: '0 4px 15px rgba(0,0,0,0.4)',
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: '#FFFFFF',
    boxShadow: '0 6px 20px rgba(0,0,0,0.6)',
    transform: 'translateY(-2px)'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAAFklEQVQIHWNkYGD4zwAFWAEIMmAEWAAAAAxjAYkAAAAASUVORK5CYII=")',
    opacity: 0.05,
    pointerEvents: 'none'
  }
}));

// Styled Button
const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '4px',
  textTransform: 'uppercase',
  padding: '10px 20px',
  fontWeight: 600,
  letterSpacing: '1.5px',
  background: 'transparent',
  color: '#FFFFFF',
  border: '1px solid #B0B0B0',
  transition: 'all 0.3s ease-in-out',
  willChange: 'transform, background, box-shadow',
  '&:hover': {
    background: '#FFFFFF',
    color: '#000000',
    border: '1px solid #FFFFFF',
    boxShadow: '0 0 15px rgba(255,255,255,0.3)',
    transform: 'translateY(-1px)'
  },
  '&.Mui-disabled': {
    borderColor: '#666666',
    color: '#666666',
    opacity: 0.6
  }
}));

// Styled Typography for subtle typing effect (retained from original)
const TypingTypography = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  borderRight: '2px solid #FFFFFF', // Changed border color for minimalist theme
  animation: 'typing 1.5s steps(30, end), blink 0.7s step-end infinite', // Adjusted blink speed
  '@keyframes typing': {
    'from': { width: '0' },
    'to': { width: '100%' }
  },
  '@keyframes blink': {
    '0%, 100%': { borderColor: '#FFFFFF' },
    '50%': { borderColor: 'transparent' }
  }
}));

// Memoized LoginCard using StyledCard
const LoginCard = memo(({ children, sx = {} }) => (
  <StyledCard
    sx={{
      p: 3,
      minHeight: '100%',
      ...sx
    }}
  >
    <CardContent sx={{ p: 0, height: '100%' }}>
      {children}
    </CardContent>
  </StyledCard>
));

// Transition component for dialogs
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function Login({ setWallet, setIsSessionActive }) {
  const [password, setPassword] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasWallet, setHasWallet] = useState(false);
  const [showTwoFA, setShowTwoFA] = useState(false);
  const [decryptedWallet, setDecryptedWallet] = useState(null);
  const [twoFASecret, setTwoFASecret] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [passwordError, setPasswordError] = useState(''); // Estado para mostrar error de contraseña
  const [twoFAError, setTwoFAError] = useState(''); // Estado para mostrar error de 2FA
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar(); // notistack enqueueSnackbar

  const REQUIRED_CONFIRMATION_TEXT = 'RESET WALLET';

  // No sound effect functions as sound option is removed

  useEffect(() => {
    const checkWalletAndSession = async () => {
      const encryptedWallet = await getFromStorage('cryptoWallet');
      setHasWallet(!!encryptedWallet);
    };
    checkWalletAndSession();

    // Session timeout logic
    const timeout = setTimeout(async () => {
      await saveToStorage('sessionActive', 'false');
      setWallet(null);
      setIsSessionActive(false);
      enqueueSnackbar(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypingTypography>SESSION EXPIRED DUE TO INACTIVITY</TypingTypography>
        </Box>,
        {
          variant: 'info',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          style: { background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #333333', borderRadius: '4px' }
        }
      );
      navigate('/login');
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearTimeout(timeout);
  }, [navigate, setWallet, setIsSessionActive, enqueueSnackbar]);

  // Función para limpiar datos corruptos
  const clearCorruptedData = async () => {
    try {
      console.log('🧹 Limpiando datos corruptos...');
      await clearSecureStorage();
      enqueueSnackbar(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypingTypography>CORRUPTED DATA CLEARED - PLEASE CREATE NEW WALLET</TypingTypography>
        </Box>,
        {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          style: { background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #333333', borderRadius: '4px' }
        }
      );
      setHasWallet(false);
      navigate('/create-wallet');
    } catch (error) {
      console.error('❌ Error limpiando datos:', error);
    }
  };

  const validatePassword = async () => {
    try {
      const encryptedWallet = await getFromStorage('cryptoWallet');
      
      if (!encryptedWallet) {
        throw new Error('WALLET DATA NOT FOUND');
      }
      
      const decrypted = await decryptData(encryptedWallet, password);
      const walletObj = JSON.parse(decrypted);
      
      if (!walletObj.address || !ethers.utils.isAddress(walletObj.address)) {
        throw new Error('INVALID WALLET ADDRESS');
      }
      
      if (!walletObj.privateKey) {
        throw new Error('PRIVATE KEY NOT FOUND');
      }

      return walletObj;
    } catch (err) {
      console.error('PASSWORD VALIDATION ERROR:', err);
      return null;
    }
  };

  const validateTwoFACode = (code, secret) => {
    try {
      const isValid = authenticator.verify({ token: code, secret });
      return isValid;
    } catch (err) {
      console.error('❌ 2FA VALIDATION ERROR:', err);
      return false;
    }
  };

  const handlePasswordSubmit = async () => {
    // Limpiar error previo
    setPasswordError('');
    
    if (!password) {
      setPasswordError('⚠️ DEBES INGRESAR UNA CONTRASEÑA');
      enqueueSnackbar(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning sx={{ color: '#FFD700' }} />
          <TypingTypography>ENTER WALLET PASSWORD</TypingTypography>
        </Box>,
        {
          variant: 'warning',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          style: { background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #FFD700', borderRadius: '4px' }
        }
      );
      return;
    }

    setIsLoading(true);
    try {
      const wallet = await validatePassword();
      if (!wallet) {
        // Mostrar error VISIBLE en pantalla
        setPasswordError('⚠️ CONTRASEÑA INCORRECTA - INTENTA DE NUEVO');
        
        enqueueSnackbar(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#FF6B6B', fontSize: 24 }} />
            <Typography sx={{ fontWeight: 600, letterSpacing: '1px', color: '#FFFFFF', fontSize: '1rem' }}>
              ⚠️ CONTRASEÑA INCORRECTA - INTENTA DE NUEVO
            </Typography>
          </Box>,
          {
            variant: 'error',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            autoHideDuration: 5000,
            style: { 
              background: 'rgba(255, 107, 107, 0.2)', 
              color: '#FFFFFF', 
              border: '2px solid #FF6B6B', 
              borderRadius: '4px',
              padding: '16px 24px',
              boxShadow: '0 4px 20px rgba(255, 107, 107, 0.3)'
            }
          }
        );
        setPassword(''); // Limpiar el campo de contraseña
        setIsLoading(false);
        return;
      }

      // Si la contraseña es correcta, limpiar cualquier error
      setPasswordError('');
      
      const secret = await getFromStorage(`2faSecret_${wallet.address}`);
      
      if (!secret) {
        enqueueSnackbar(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#FFD700' }} />
            <TypingTypography>2FA SECRET NOT FOUND. SETUP REQUIRED.</TypingTypography>
          </Box>,
          {
            variant: 'warning',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            style: { background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #FFD700', borderRadius: '4px' },
            onClose: () => navigate('/setup-2fa')
          }
        );
        setIsLoading(false);
        return;
      }

      setDecryptedWallet(wallet);
      setTwoFASecret(secret);
      setShowTwoFA(true);
      setIsLoading(false);
    } catch (error) {
      console.error('PASSWORD VALIDATION ERROR:', error);
      
      // Mensaje específico de error
      let errorMessage = '⚠️ CONTRASEÑA INCORRECTA - INTENTA DE NUEVO';
      if (error.message && error.message.includes('WALLET DATA NOT FOUND')) {
        errorMessage = '❌ WALLET NO ENCONTRADO - CREA UNO NUEVO';
      } else if (error.message && error.message.includes('INVALID')) {
        errorMessage = '❌ DATOS CORRUPTOS - RESETEA LA WALLET';
      }
      
      // Mostrar error VISIBLE en pantalla
      setPasswordError(errorMessage);
      
      enqueueSnackbar(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning sx={{ color: '#FF6B6B', fontSize: 24 }} />
          <Typography sx={{ fontWeight: 600, letterSpacing: '1px', color: '#FFFFFF', fontSize: '1rem' }}>
            {errorMessage}
          </Typography>
        </Box>,
        {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          autoHideDuration: 5000,
          style: { 
            background: 'rgba(255, 107, 107, 0.2)', 
            color: '#FFFFFF', 
            border: '2px solid #FF6B6B', 
            borderRadius: '4px',
            padding: '16px 24px',
            boxShadow: '0 4px 20px rgba(255, 107, 107, 0.3)'
          }
        }
      );
      setPassword(''); // Limpiar el campo de contraseña
      setIsLoading(false);
    }
  };

  const handleTwoFASubmit = async () => {
    // Limpiar error previo
    setTwoFAError('');
    
    if (!twoFACode) {
      setTwoFAError('⚠️ DEBES INGRESAR EL CÓDIGO 2FA');
      enqueueSnackbar(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypingTypography>ENTER 2FA CODE</TypingTypography>
        </Box>,
        {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          style: { background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #333333', borderRadius: '4px' }
        }
      );
      return;
    }
    
    setIsLoading(true);
    try {
      if (!validateTwoFACode(twoFACode, twoFASecret)) {
        // Mostrar error VISIBLE en pantalla
        setTwoFAError('⚠️ CÓDIGO 2FA INCORRECTO - INTENTA DE NUEVO');
        
        enqueueSnackbar(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#FF6B6B', fontSize: 24 }} />
            <Typography sx={{ fontWeight: 600, letterSpacing: '1px', color: '#FFFFFF', fontSize: '1rem' }}>
              ⚠️ CÓDIGO 2FA INCORRECTO - INTENTA DE NUEVO
            </Typography>
          </Box>,
          {
            variant: 'error',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            autoHideDuration: 5000,
            style: { 
              background: 'rgba(255, 107, 107, 0.2)', 
              color: '#FFFFFF', 
              border: '2px solid #FF6B6B', 
              borderRadius: '4px',
              padding: '16px 24px',
              boxShadow: '0 4px 20px rgba(255, 107, 107, 0.3)'
            }
          }
        );
        setTwoFACode(''); // Limpiar el campo de 2FA
        setIsLoading(false);
        return;
      }

      // Si el código 2FA es correcto, limpiar cualquier error
      setTwoFAError('');
      
      setWallet(decryptedWallet);
      setIsSessionActive(true);
      await saveToStorage('sessionActive', 'true');
      
      // Guardar privateKey y mnemonic en localStorage para persistencia de sesión
      if (decryptedWallet.privateKey) {
        localStorage.setItem('privateKey', decryptedWallet.privateKey);
      }
      if (decryptedWallet.mnemonic) {
        localStorage.setItem('mnemonic', decryptedWallet.mnemonic);
      }
      if (decryptedWallet.address) {
        localStorage.setItem('address', decryptedWallet.address);
      }
      
      enqueueSnackbar(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypingTypography>LOGIN SUCCESSFUL</TypingTypography>
        </Box>,
        {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          style: { background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #333333', borderRadius: '4px' }
        }
      );
      
      navigate('/dashboard');
    } catch (error) {
      console.error('2FA VALIDATION ERROR:', error);
      enqueueSnackbar(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypingTypography>ERROR VALIDATING 2FA CODE</TypingTypography>
        </Box>,
        {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          style: { background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #333333', borderRadius: '4px' }
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetWallet = async () => {
    if (confirmationText !== REQUIRED_CONFIRMATION_TEXT) {
      enqueueSnackbar(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypingTypography>TYPE "{REQUIRED_CONFIRMATION_TEXT}" TO CONFIRM</TypingTypography>
        </Box>,
        {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          style: { background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #333333', borderRadius: '4px' }
        }
      );
      return;
    }

    setIsLoading(true);
    try {
      await clearSecureStorage();
      enqueueSnackbar(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypingTypography>WALLET DATA CLEARED. REDIRECTING.</TypingTypography>
        </Box>,
        {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          style: { background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #333333', borderRadius: '4px' },
          autoClose: 2000,
          onClose: () => {
            setWallet(null);
            setIsSessionActive(false);
            setPassword('');
            setTwoFACode('');
            setShowTwoFA(false);
            setDecryptedWallet(null);
            setTwoFASecret('');
            setHasWallet(false);
            setConfirmationText('');
            setResetDialogOpen(false);
            navigate('/');
          }
        }
      );
    } catch (error) {
      console.error('RESET WALLET ERROR:', error);
      setIsLoading(false);
      enqueueSnackbar(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypingTypography>ERROR RESETTING WALLET</TypingTypography>
        </Box>,
        {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          style: { background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #333333', borderRadius: '4px' }
        }
      );
    }
  };

  const handleLogout = async () => {
    await saveToStorage('sessionActive', 'false');
    setWallet(null);
    setIsSessionActive(false);
    setPassword('');
    setTwoFACode('');
    setShowTwoFA(false);
    setDecryptedWallet(null);
    setTwoFASecret('');
    enqueueSnackbar(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TypingTypography>LOGGED OUT SUCCESSFULLY</TypingTypography>
      </Box>,
      {
        variant: 'info',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        style: { background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #333333', borderRadius: '4px' }
      }
    );
    navigate('/login');
  };

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider
        maxSnack={3}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        hideIconVariant
        data-testid="snackbar-provider"
        Components={{
          default: ({ message, ...props }) => (
            <Box {...props} sx={{ background: 'rgba(10, 10, 10, 0.9)', color: '#FFFFFF', border: '1px solid #333333', borderRadius: '4px', fontFamily: '"Roboto Mono", monospace', padding: '12px 16px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
              {message}
            </Box>
          ),
          info: ({ message, ...props }) => (
            <Box {...props} sx={{ background: 'rgba(10, 10, 10, 0.9)', color: '#87CEEB', border: '1px solid #87CEEB', borderRadius: '4px', fontFamily: '"Roboto Mono", monospace', padding: '12px 16px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
              {message}
            </Box>
          ),
          success: ({ message, ...props }) => (
            <Box {...props} sx={{ background: 'rgba(10, 10, 10, 0.9)', color: '#8BC34A', border: '1px solid #8BC34A', borderRadius: '4px', fontFamily: '"Roboto Mono", monospace', padding: '12px 16px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
              {message}
            </Box>
          ),
          warning: ({ message, ...props }) => (
            <Box {...props} sx={{ background: 'rgba(10, 10, 10, 0.9)', color: '#FFD700', border: '1px solid #FFD700', borderRadius: '4px', fontFamily: '"Roboto Mono", monospace', padding: '12px 16px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
              {message}
            </Box>
          ),
          error: ({ message, ...props }) => (
            <Box {...props} sx={{ background: 'rgba(10, 10, 10, 0.9)', color: '#FF6B6B', border: '1px solid #FF6B6B', borderRadius: '4px', fontFamily: '"Roboto Mono", monospace', padding: '12px 16px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
              {message}
            </Box>
          ),
        }}
      >
        <Box sx={{
          minHeight: '100vh',
          bgcolor: '#000000',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          position: 'relative',
          // Subtle background texture/grain, not CRT noise
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAAFklEQVQIHWNkYGD4zwAFWAEIMmAEWAAAAAxjAYkAAAAASUVORK5CYII=")',
            opacity: 0.08, // Increased opacity for the subtle grain effect
            pointerEvents: 'none'
          }
        }}>
          {/* Removed MatrixRain component */}

          <Dialog
            open={resetDialogOpen}
            onClose={() => setResetDialogOpen(false)}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                bgcolor: 'rgba(10, 10, 10, 0.98)',
                border: '1px solid #333333',
                borderRadius: '4px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }
            }}
          >
            <DialogTitle sx={{
              bgcolor: 'rgba(10, 10, 10, 0.98)',
              color: '#FFD700', // Warning color for reset dialog title
              display: 'flex',
              alignItems: 'center',
              py: 1.5,
              borderBottom: '1px solid #333333'
            }}>
              <Warning sx={{ mr: 1, color: '#FFD700' }} />
              <Typography variant="h6" fontWeight={500} letterSpacing="1px">
                RESET WALLET
              </Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setResetDialogOpen(false)}
                sx={{ ml: 'auto', color: '#B0B0B0' }}
                aria-label="close reset dialog"
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ py: 3, bgcolor: 'rgba(10, 10, 10, 0.98)', color: '#FFFFFF' }}>
              <DialogContentText color="#B0B0B0" sx={{ mb: 3, fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                RESETTING YOUR WALLET WILL PERMANENTLY DELETE ALL DATA. TYPE <strong>{REQUIRED_CONFIRMATION_TEXT}</strong> TO CONFIRM:
              </DialogContentText>
              <TextField
                fullWidth
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="RESET WALLET"
                sx={{ mb: 3 }}
                InputProps={{
                  sx: { fontFamily: '"Roboto Mono", monospace', color: '#FFFFFF' }
                }}
                aria-label="reset confirmation"
              />
            </DialogContent>
            <DialogActions sx={{
              px: 2,
              py: 1.5,
              borderTop: '1px solid #333333',
              bgcolor: 'rgba(10, 10, 10, 0.98)'
            }}>
              <StyledButton
                onClick={() => {
                  setResetDialogOpen(false);
                  setConfirmationText('');
                }}
                aria-label="cancel reset"
              >
                CANCEL
              </StyledButton>
              <StyledButton
                onClick={handleResetWallet}
                disabled={isLoading}
                aria-label="confirm reset"
              >
                {isLoading ? <CircularProgress size={24} sx={{ color: '#B0B0B0' }} /> : 'RESET'}
              </StyledButton>
            </DialogActions>
          </Dialog>

          <AppBar
            position="static"
            elevation={0}
            sx={{ 
              backgroundColor: '#000000',
              borderBottom: '1px solid #FFFFFF',
              boxShadow: 'none'
            }}
          >
            <Toolbar sx={{ justifyContent: 'space-between', py: 1.5, px: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#FFFFFF',
                  textTransform: 'uppercase'
                }}
              >
                AVO_STATION
              </Typography>
              
              {/* Botones del menú directamente en el header */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {!hasWallet && (
                  <Button
                    startIcon={<Wallet />}
                    onClick={() => navigate('/create-wallet')}
                    sx={{
                      color: '#FFFFFF',
                      border: '1px solid #FFFFFF',
                      borderRadius: 0,
                      px: 2,
                      py: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#FFFFFF',
                        color: '#000000'
                      }
                    }}
                  >
                    CREATE WALLET
                  </Button>
                )}
              </Box>
            </Toolbar>
          </AppBar>

          <Box sx={{
            maxWidth: 500, // Adjusted max width for a more focused minimalist card
            mx: 'auto',
            px: 2,
            py: 4,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  textAlign: 'center',
                  color: '#FFFFFF'
                }}
              >
                AVO_STATION
              </Typography>

              {isLoading ? (
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <LoginCard sx={{ textAlign: 'center', p: 3 }}>
                      <CircularProgress sx={{ color: '#FFFFFF' }} />
                      <Typography variant="body1" sx={{ mt: 2, color: '#B0B0B0', letterSpacing: '0.8px' }}>
                        AUTHENTICATING...
                      </Typography>
                    </LoginCard>
                  </motion.div>
                </AnimatePresence>
              ) : (
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <LoginCard>
                      <CardContent>
                        <Typography variant="h5" sx={{
                          fontWeight: 700,
                          color: '#FFFFFF',
                          mb: 2
                        }}>
                          {hasWallet ? 'AUTHENTICATE' : 'INITIALIZE'}
                        </Typography>
                        {hasWallet ? (
                          <>
                            {!showTwoFA ? (
                              <>
                                <TextField
                                  fullWidth
                                  label="PASSWORD"
                                  type="password"
                                  value={password}
                                  onChange={(e) => {
                                    setPassword(e.target.value);
                                    setPasswordError(''); // Limpiar error al escribir
                                  }}
                                  placeholder="ENTER PASSWORD"
                                  InputProps={{
                                    startAdornment: (
                                      <Key sx={{ mr: 1, color: '#B0B0B0' }} />
                                    ),
                                    sx: { color: '#FFFFFF' }
                                  }}
                                  sx={{ mb: passwordError ? 1 : 2 }}
                                  aria-label="wallet password"
                                  error={!!passwordError}
                                />
                                
                                {/* Mensaje de Error Visible */}
                                {passwordError && (
                                  <Alert 
                                    severity="error" 
                                    icon={<Warning sx={{ fontSize: 24 }} />}
                                    sx={{ 
                                      mb: 2,
                                      backgroundColor: 'rgba(255, 107, 107, 0.15)',
                                      border: '2px solid #FF6B6B',
                                      color: '#FFFFFF',
                                      '& .MuiAlert-icon': {
                                        color: '#FF6B6B'
                                      },
                                      '& .MuiAlert-message': {
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.5px'
                                      }
                                    }}
                                  >
                                    {passwordError}
                                  </Alert>
                                )}
                                
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#B0B0B0',
                                    textAlign: 'right',
                                    mb: 2,
                                    cursor: 'pointer',
                                    '&:hover': { color: '#FFFFFF' }
                                  }}
                                  onClick={() => setResetDialogOpen(true)}
                                >
                                  DELETE WALLET?
                                </Typography>
                                <StyledButton
                                  startIcon={<Key />}
                                  onClick={handlePasswordSubmit}
                                  disabled={isLoading}
                                  fullWidth
                                  aria-label="validate password"
                                >
                                  VALIDATE
                                </StyledButton>
                              </>
                            ) : (
                              <>
                                <TextField
                                  fullWidth
                                  label="2FA CODE"
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  maxLength={6}
                                  value={twoFACode}
                                  onChange={(e) => {
                                    if (/^\d*$/.test(e.target.value)) {
                                      setTwoFACode(e.target.value);
                                    }
                                  }}
                                  placeholder="ENTER CODE"
                                  InputProps={{
                                    startAdornment: (
                                      <Lock sx={{ mr: 1, color: '#B0B0B0' }} />
                                    ),
                                    sx: { color: '#FFFFFF' }
                                  }}
                                  sx={{ mb: 2 }}
                                  aria-label="2FA code"
                                  error={!!twoFAError}
                                />
                                
                                {/* Mensaje de Error Visible para 2FA */}
                                {twoFAError && (
                                  <Alert 
                                    severity="error" 
                                    icon={<Warning sx={{ fontSize: 24 }} />}
                                    sx={{ 
                                      mb: 2,
                                      backgroundColor: 'rgba(255, 107, 107, 0.15)',
                                      border: '2px solid #FF6B6B',
                                      color: '#FFFFFF',
                                      '& .MuiAlert-icon': {
                                        color: '#FF6B6B'
                                      },
                                      '& .MuiAlert-message': {
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.5px'
                                      }
                                    }}
                                  >
                                    {twoFAError}
                                  </Alert>
                                )}
                                
                                <StyledButton
                                  startIcon={<Lock />}
                                  onClick={handleTwoFASubmit}
                                  disabled={isLoading || twoFACode.length !== 6}
                                  fullWidth
                                  aria-label="authenticate 2FA"
                                >
                                  AUTHENTICATE
                                </StyledButton>
                              </>
                            )}
                          </>
                        ) : (
                          <StyledButton
                            startIcon={<Wallet />}
                            component={Link}
                            to="/create-wallet"
                            fullWidth
                            aria-label="create wallet"
                          >
                            CREATE WALLET
                          </StyledButton>
                        )}
                      </CardContent>
                    </LoginCard>
                  </motion.div>
                </AnimatePresence>
              )}
            </motion.div>
          </Box>
        </Box>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default Login;